// ════════════════════════════════════════════════════════════════════════════
// LpaSectionHeader — header de seção dentro de página (h2 + optional CTA)
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export interface LpaSectionHeaderProps {
  title: string;
  /** Subtítulo descritivo — texto natural sans (não usar UPPERCASE) */
  sub?: string;
  /** Tag semântica (h2 default, h3 para subseções) */
  as?: "h2" | "h3";
  /** CTA discreto à direita (link/botão de "ver tudo") */
  cta?: React.ReactNode;
  /** Conteúdo extra à direita (badges, contadores) */
  extras?: React.ReactNode;
  /** Estilo do sub — "prose" (default) ou "eyebrow" (mono caps) */
  subStyle?: "prose" | "eyebrow";
  className?: string;
  style?: React.CSSProperties;
}

export default function LpaSectionHeader({
  title,
  sub,
  as: Tag = "h2",
  cta,
  extras,
  subStyle = "prose",
  className,
  style,
}: LpaSectionHeaderProps): React.ReactElement {
  const isH3 = Tag === "h3";
  return (
    <div
      className={className}
      style={{
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        marginTop: 28,
        marginBottom: 16,
        gap: 12,
        flexWrap: "wrap",
        ...style,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <Tag
          style={
            isH3
              ? {
                  fontFamily: 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif',
                  fontSize: 16,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  letterSpacing: "-0.005em",
                  color: "var(--lpa-text)",
                  margin: 0,
                }
              : {
                  fontFamily:
                    '"Playfair Display", "Cormorant Garamond", "Garamond", Georgia, serif',
                  fontSize: 22,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  letterSpacing: "-0.015em",
                  color: "var(--lpa-text)",
                  margin: 0,
                }
          }
        >
          {title}
        </Tag>
        {sub && (
          <div
            style={
              subStyle === "eyebrow"
                ? {
                    fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "var(--lpa-muted)",
                    marginTop: 4,
                  }
                : {
                    fontFamily: 'Inter, -apple-system, "Segoe UI", system-ui, sans-serif',
                    fontSize: 12,
                    fontWeight: 400,
                    lineHeight: 1.5,
                    color: "var(--lpa-sub)",
                    marginTop: 4,
                  }
            }
          >
            {sub}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {extras}
        {cta}
      </div>
    </div>
  );
}
